import React, { useState, useEffect } from 'react';

const DISMISSED_KEY = 'shkalot_install_prompt_dismissed';
const DISMISS_DURATION_DAYS = 7;

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedDate = new Date(parseInt(dismissedAt, 10));
      const daysSinceDismissed = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
        return; // Don't show if dismissed within last 7 days
      }
    }

    // Listen for beforeinstallprompt (Android Chrome/Edge)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show prompt after a delay if not standalone
    if (iOS && !standalone) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome - trigger native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed or shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-white border-4 border-black rounded-2xl p-4 shadow-hard">
        <div className="flex items-start gap-3">
          <img 
            src="/icon-192.png" 
            alt="Shkalot" 
            className="w-12 h-12 rounded-xl border-2 border-black"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-black text-lg">התקן את שקלות</h3>
            <p className="text-sm text-gray-600 mt-1">
              {isIOS 
                ? 'לחץ על כפתור השיתוף ואז "הוסף למסך הבית"'
                : 'הוסף לטלפון לגישה מהירה'}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            aria-label="סגור"
          >
            ✕
          </button>
        </div>
        
        {isIOS ? (
          // iOS instructions
          <div className="mt-3 flex items-center justify-center gap-2 text-sm bg-gray-100 rounded-lg p-3 border-2 border-black">
            <span>לחץ</span>
            <span className="text-2xl">⎙</span>
            <span>ואז</span>
            <span className="font-bold">"הוסף למסך הבית"</span>
          </div>
        ) : (
          // Android/Chrome install button
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100 transition-colors"
            >
              לא עכשיו
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary text-black shadow-hard-sm hover:opacity-90 transition-opacity"
            >
              התקן
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default InstallPrompt;
